import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { FaBars, FaTimes, FaStar, FaStarHalfAlt } from 'react-icons/fa'
import { FcGoogle } from 'react-icons/fc'
import Logo from '@/components/common/Logo'

const Header = () => {
  const { t } = useTranslation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: 'Wunschkennzeichen', href: '/wunschkennzeichen' },
    { name: t('nav.shop'), href: '/shop' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.contact'), href: '/contact' },
  ]

  const isActive = (path: string) => location.pathname === path

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="sticky top-0 z-50 transition-all duration-300 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm"
    >
      <div className="relative container-custom px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-2 sm:gap-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Logo />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2 flex-grow justify-center">
            {navigation.map((item, index) => {
              const active = isActive(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="relative group"
                >
                  <motion.span
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className={`
                      relative px-5 py-2.5 rounded-full text-[15px] font-semibold transition-all duration-300 flex items-center
                      ${active
                        ? 'text-primary-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50/80'
                      }
                    `}
                  >
                    {item.name}
                  </motion.span>

                  {/* Active indicator */}
                  {active && (
                    <motion.div
                      layoutId="activeNavBackground"
                      className="absolute inset-0 bg-primary-50 rounded-full -z-10 border border-primary-100/50"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              )
            })}
          </nav>

          {/* Trust Badges */}
          <div className="hidden xl:flex items-center gap-5 ml-2 mr-2">
            {/* SSL */}
            <div className="flex items-center shrink-0">
              <img src="/assets/ssl-logo.png" alt="SSL Datensicherheit" className="h-[42px] w-auto object-contain" />
            </div>

            {/* DIN */}
            <a href="https://www.dincertco.tuv.com/registrations/60168568?locale=de" target="_blank" rel="noopener noreferrer" className="shrink-0 hover:scale-105 transition-transform duration-300">
              <img src="/assets/DIN.png" alt="DIN Geprüft" className="h-[42px] w-auto object-contain" />
            </a>

            {/* Google Reviews */}
            <a href="https://www.google.com/maps/place/Deutsche+Auto+Digital+GmbH/@51.2486797,6.5927967,17z/data=!3m1!4b1!4m6!3m5!1s0x4fd5b689d7e90d4b:0x2544598bf640b426!8m2!3d51.2486797!4d6.5927967!16s%2Fg%2F11yx__wp6l?entry=ttu&g_ep=EgoyMDI2MDIxOC4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" className="shrink-0 flex flex-col items-center justify-center hover:scale-105 transition-transform duration-300">
              <FcGoogle className="w-[22px] h-[22px] mb-[2px]" />
              <div className="flex items-center gap-[3px]">
                <span className="text-gray-900 font-extrabold text-[12px] leading-none mt-[1px]">4.6</span>
                <div className="flex text-[#FFC107] text-[10px] gap-[1px]">
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStarHalfAlt />
                </div>
              </div>
            </a>
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">


            {/* Mobile menu button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 sm:p-2.5 rounded-lg sm:rounded-xl text-gray-700 hover:text-black transition-all duration-300"
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {mobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaBars className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileMenuOpen(false)}
                className="fixed inset-0 top-20 bg-black/20 backdrop-blur-sm lg:hidden"
              />

              {/* Menu */}
              <motion.div
                initial={{ opacity: 0, height: 0, y: -20 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0, y: -20 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="lg:hidden overflow-hidden border-t border-gray-200 bg-white"
              >
                <nav className="flex flex-col py-2">
                  {navigation.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`
                          relative block px-6 py-3.5 text-base font-semibold transition-all duration-300
                          ${isActive(item.href)
                            ? 'text-primary-600 bg-primary-50/50 border-r-4 border-primary-500'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50/80'
                          }
                        `}
                      >
                        {item.name}
                        {isActive(item.href) && (
                          <motion.div
                            layoutId="mobileActiveNav"
                            className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500 rounded-r-full"
                            initial={false}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          />
                        )}
                      </Link>
                    </motion.div>
                  ))}
                </nav>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>


    </motion.header>
  )
}

export default Header
